import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import itPortalService from "../services/itPortalService";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  MdEdit,
  MdDelete,
  MdOutlineAccountBalanceWallet,
  MdOutlineAdminPanelSettings,
  MdOutlineReceiptLong,
} from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ManageVatConfig() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Notification state
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "UK Vat",
    rate: 20.0,
    status: "active",
    notes: "",
  });

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await itPortalService.getVatConfigs();
      const list = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRecords(list);
    } catch (err) {
      console.error("Failed to load VAT configurations:", err);
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        showSnack(
          err.response?.data?.message || err.message || "Failed to load VAT configurations.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      name: record.name,
      rate: record.rate ?? 20.0,
      status: record.status || "active",
      notes: record.notes || "",
    });
    setIsEditModalOpen(true);
  };

  // Open Add Modal
  const handleOpenAdd = () => {
    setFormData({
      name: "",
      rate: 20.0,
      status: "active",
      notes: "",
    });
    setIsAddModalOpen(true);
  };

  // Open Delete Modal
  const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.updateVatConfig(selectedRecord.id, {
        name: formData.name,
        rate: parseFloat(formData.rate),
        status: formData.status,
        notes: formData.notes || null,
      });

      showSnack(`"${formData.name}" configuration updated successfully!`);
      setIsEditModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(" ")
          : err.response?.data?.message || "Failed to update VAT configuration.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Save Add
  const handleSaveAdd = async (e) => {
    e.preventDefault();

    setIsSaving(true);
    try {
      await itPortalService.createVatConfig({
        name: formData.name,
        rate: parseFloat(formData.rate),
        status: formData.status,
        notes: formData.notes || null,
      });

      showSnack(`"${formData.name}" configuration added successfully!`);
      setIsAddModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(" ")
          : err.response?.data?.message || "Failed to add VAT configuration.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.deleteVatConfig(selectedRecord.id);
      showSnack(`"${selectedRecord.name}" configuration removed successfully.`);
      setIsDeleteModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Failed to delete configuration.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered records
  const filteredRecords = records.filter((r) =>
    r.name?.toLowerCase().includes(search.toLowerCase())
  );

  const ukVatRecord = records.find(
    (r) => r.name?.toLowerCase().includes("uk") || r.name?.toLowerCase().includes("vat")
  );

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        {/* Header Bar matching SuperAdmin style */}
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">VAT Configuration Management</h2>
            <div className="it-header-actions">
              <button className="action-btn" onClick={handleOpenAdd}>
                + Add VAT Rate
              </button>
            </div>
          </div>

          {/* IT Portal Subnavigation Tabs */}
          {/* <div className="it-tabs-container">
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/user-topcoin")}
            >
              <MdOutlineAccountBalanceWallet size={18} /> User TopCoin Management
            </button>
            <button
              className="it-tab-item active"
              onClick={() => navigate("/it-portal/vat-config")}
            >
              <MdOutlineReceiptLong size={18} /> VAT Configuration
            </button>
            <button
              className="it-tab-item"
              onClick={() => navigate("/it-portal/dashboard?tab=security")}
            >
              <MdOutlineAdminPanelSettings size={18} /> Security Settings
            </button>
          </div> */}

          {/* Quick Statistics matching SuperAdmin style */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Configurations:</span>
              <span className="stat-value">{records.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">UK VAT Rate:</span>
              <span className="stat-value" style={{ color: "#701a75" }}>
                {ukVatRecord ? `${Number(ukVatRecord.rate).toFixed(2)}%` : "20.00%"}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Active Rates:</span>
              <span className="stat-value">
                {records.filter((r) => r.status === "active").length}
              </span>
            </div>
          </div>

          {/* Filter Bar matching SuperAdmin style */}
          {/* <div className="filters">
            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search VAT Configuration:</label>
              <input
                type="text"
                placeholder="Search by configuration name (e.g. UK Vat)..."
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={30}
              />
            </div>
          </div> */}
        </div>

        {/* Table Container & Member Table matching SuperAdmin style */}
        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "70px" }}>ID</th>
                <th style={{ width: "240px" }}>Configuration Name</th>
                <th style={{ width: "160px" }}>VAT Rate (%)</th>
                {/* <th style={{ width: "160px" }}>Rate Multiplier</th> */}
                <th style={{ width: "140px" }}>Status</th>
                <th>Notes</th>
                <th style={{ width: "160px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "30px" }}>
                    Loading VAT configurations...
                  </td>
                </tr>
              ) : filteredRecords.length > 0 ? (
                filteredRecords.map((item) => (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 600 }}>{item.id}</td>
                    <td>
                      <span className="it-badge it-badge-retail" style={{ fontWeight: 700 }}>
                        {item.name}
                      </span>
                    </td>
                    <td>
                      <strong style={{ color: "#701a75", fontSize: "15px" }}>
                        {Number(item.rate).toFixed(2)}%
                      </strong>
                    </td>
                    {/* <td>
                      <span style={{ color: "#059669", fontWeight: 700 }}>
                        {(Number(item.rate) / 100).toFixed(4)}
                      </span>
                    </td> */}
                    <td>
                      <span
                        className={`it-badge ${
                          item.status === "active" ? "it-badge-senior-agent" : "it-badge-default"
                        }`}
                        style={{ textTransform: "capitalize" }}
                      >
                        {item.status || "active"}
                      </span>
                    </td>
                    <td style={{ color: item.notes ? "#334155" : "#94a3b8" }}>
                      {item.notes || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="it-table-actions">
                        <button
                          className="it-btn-edit"
                          title="Edit configuration"
                          onClick={() => handleOpenEdit(item)}
                        >
                          <MdEdit size={14} /> Edit
                        </button>
                        <button
                          className="it-btn-delete"
                          title="Delete configuration"
                          onClick={() => handleOpenDelete(item)}
                        >
                          <MdDelete size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="it-empty-state">
                      No VAT configurations found matching your search.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Edit VAT Configuration: {selectedRecord?.name}</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>Configuration Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g. UK Vat"
                    required
                  />
                </div>

                <div className="it-form-field">
                  <label>VAT Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.rate}
                    onChange={(e) =>
                      setFormData({ ...formData, rate: e.target.value })
                    }
                    placeholder="e.g. 20.00"
                    required
                  />
                  <small>Standard UK VAT is 20%. Entered as percentage (e.g. 20 = 20%).</small>
                </div>

                <div className="it-form-field">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="it-form-field">
                  <label>Notes (Optional)</label>
                  <textarea
                    rows="3"
                    placeholder="Additional details (e.g. Standard UK Value Added Tax rate)..."
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: "0px" }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Add New VAT Configuration</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsAddModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveAdd}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>Configuration Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g. UK Vat"
                    required
                  />
                </div>

                <div className="it-form-field">
                  <label>VAT Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.rate}
                    onChange={(e) =>
                      setFormData({ ...formData, rate: e.target.value })
                    }
                    placeholder="e.g. 20.00"
                    required
                  />
                  <small>Standard UK VAT is 20%. Entered as percentage (e.g. 20 = 20%).</small>
                </div>

                <div className="it-form-field">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="it-form-field">
                  <label>Notes (Optional)</label>
                  <textarea
                    rows="3"
                    placeholder="Additional details..."
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: "0px" }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Add VAT Rate"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box it-delete-modal-box">
            <div className="it-modal-header">
              <h3>Delete VAT Configuration</h3>
              <button
                className="it-modal-close"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="it-modal-body">
              <p>
                Are you sure you want to delete the VAT configuration for{" "}
                <strong>"{selectedRecord?.name}"</strong>?
              </p>
              <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "8px" }}>
                Warning: TopCoin purchase calculations depend on an active VAT rate.
              </p>
            </div>
            <div className="it-modal-footer">
              <button
                type="button"
                className="it-btn-cancel"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="it-btn-delete-confirm"
                onClick={handleConfirmDelete}
                disabled={isSaving}
              >
                {isSaving ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

